import {
  useId,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { Eye, EyeOff } from 'lucide-react';
import css from './Campo.module.css';
import { cx } from './cx';

interface Moldura {
  rotulo: string;
  dica?: ReactNode;
  erro?: string;
  /** Algo à direita do rótulo ("Esqueci a senha"). */
  acessorio?: ReactNode;
  opcional?: boolean;
  /** Rótulo só para leitor de tela (filtros com placeholder óbvio). */
  rotuloOculto?: boolean;
}

/** Rótulo, controle e a linha de dica/erro — a mesma moldura para todo campo. */
function Grupo({
  id,
  moldura,
  grande,
  comIcone,
  comAcao,
  className,
  children,
}: {
  id: string;
  moldura: Moldura;
  grande?: boolean;
  comIcone?: boolean;
  comAcao?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const { rotulo, dica, erro, acessorio, opcional, rotuloOculto } = moldura;
  return (
    <div
      className={cx(
        css.grupo,
        grande && css.grande,
        comIcone && css.comIcone,
        comAcao && css.comAcao,
        erro && css.invalido,
        className,
      )}
    >
      <div className={cx(css.cabecalho, rotuloOculto && 'sr-only')}>
        <label htmlFor={id} className={css.rotulo}>
          {rotulo}
          {opcional && <span className={css.opcional}> (opcional)</span>}
        </label>
        {acessorio}
      </div>
      {children}
      {erro ? (
        // `role="alert"`: o leitor de tela anuncia o erro na hora.
        <span id={`${id}-msg`} role="alert" className={css.erro}>
          {erro}
        </span>
      ) : dica ? (
        <span id={`${id}-msg`} className={css.dica}>
          {dica}
        </span>
      ) : null}
    </div>
  );
}

interface PropsCampo extends Moldura, InputHTMLAttributes<HTMLInputElement> {
  /** Ícone à esquerda, dentro do campo (lupa da busca). */
  icone?: ReactNode;
  grande?: boolean;
  ref?: Ref<HTMLInputElement>;
}

export function Campo({
  rotulo,
  dica,
  erro,
  acessorio,
  opcional,
  rotuloOculto,
  icone,
  grande,
  className,
  ref,
  ...resto
}: PropsCampo) {
  const id = useId();
  // Todo campo de senha ganha o "mostrar": errar a senha por um caractere que
  // não dá para conferir é a causa nº 1 de login recusado.
  const ehSenha = resto.type === 'password';
  const [revelada, setRevelada] = useState(false);

  return (
    <Grupo
      id={id}
      moldura={{ rotulo, dica, erro, acessorio, opcional, rotuloOculto }}
      grande={grande}
      comIcone={!!icone}
      comAcao={ehSenha}
      className={className}
    >
      <div className={css.caixa}>
        {icone && <span className={css.icone}>{icone}</span>}
        <input
          {...resto}
          ref={ref}
          id={id}
          type={ehSenha && revelada ? 'text' : resto.type}
          aria-invalid={erro ? true : undefined}
          aria-describedby={
            [erro || dica ? `${id}-msg` : null, resto['aria-describedby']]
              .filter(Boolean)
              .join(' ') || undefined
          }
          className={css.controle}
        />
        {ehSenha && (
          <button
            type="button"
            className={css.acao}
            onClick={() => setRevelada((v) => !v)}
            aria-pressed={revelada}
            aria-label={revelada ? 'Ocultar senha' : 'Mostrar senha'}
            title={revelada ? 'Ocultar senha' : 'Mostrar senha'}
            // Fora da tabulação: do campo, o teclado vai direto ao "Entrar".
            tabIndex={-1}
          >
            {revelada ? <EyeOff /> : <Eye />}
          </button>
        )}
      </div>
    </Grupo>
  );
}

interface PropsArea extends Moldura, TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Monoespaçada: chaves de acesso, onde 0/O e 1/l precisam ser distintos. */
  mono?: boolean;
}

export function AreaTexto({
  rotulo,
  dica,
  erro,
  acessorio,
  opcional,
  rotuloOculto,
  mono = true,
  className,
  ...resto
}: PropsArea) {
  const id = useId();
  return (
    <Grupo
      id={id}
      moldura={{ rotulo, dica, erro, acessorio, opcional, rotuloOculto }}
      className={className}
    >
      <textarea
        {...resto}
        id={id}
        aria-invalid={erro ? true : undefined}
        aria-describedby={erro || dica ? `${id}-msg` : undefined}
        className={cx(css.controle, css.area, mono && css.areaMono)}
      />
    </Grupo>
  );
}

interface PropsSelecao extends Moldura, SelectHTMLAttributes<HTMLSelectElement> {
  children: ReactNode;
}

export function Selecao({
  rotulo,
  dica,
  erro,
  acessorio,
  opcional,
  rotuloOculto,
  className,
  children,
  ...resto
}: PropsSelecao) {
  const id = useId();
  return (
    <Grupo
      id={id}
      moldura={{ rotulo, dica, erro, acessorio, opcional, rotuloOculto }}
      className={className}
    >
      <select
        {...resto}
        id={id}
        aria-invalid={erro ? true : undefined}
        className={cx(css.controle, css.selecao)}
      >
        {children}
      </select>
    </Grupo>
  );
}

interface PropsMarcavel extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  children: ReactNode;
  descricao?: ReactNode;
  /** Em cartão clicável, para escolhas com mais texto. */
  cartao?: boolean;
}

/** Caixa de seleção com o texto clicável. */
export function CaixaSelecao({ children, descricao, cartao, className, ...resto }: PropsMarcavel) {
  return (
    <label className={cx(css.marcavel, cartao && css.opcaoCartao, className)}>
      <input {...resto} type="checkbox" />
      <span className={css.marcavelTexto}>
        <span>{children}</span>
        {descricao && <span className={css.marcavelDescricao}>{descricao}</span>}
      </span>
    </label>
  );
}

/** Opção de um grupo (`name` igual nas opções). */
export function Opcao({ children, descricao, cartao, className, ...resto }: PropsMarcavel) {
  return (
    <label className={cx(css.marcavel, cartao && css.opcaoCartao, className)}>
      <input {...resto} type="radio" />
      <span className={css.marcavelTexto}>
        <span>{children}</span>
        {descricao && <span className={css.marcavelDescricao}>{descricao}</span>}
      </span>
    </label>
  );
}
